import React, {useRef, useState} from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  COMMON_MFA_PREFERRED,
  COMMON_MFA_PREFERREDS,
  COMMON_SEND_MFA_PHONE_OTP,
  COMMON_VERIFY_MFA_PHONE_OTP,
} from "../../../commonUtils/globalAPISettings";
import {postRequest, setHeader} from "../../../repository/genericRepository";
import {store} from "../../../redux/store";
import {mFADialogStyle} from "./MFADialogStyles";
import {Loader} from "components/FormComponents/Loader/Loader";
import DialogTitleCommon, {DialogCloseCommon} from "../DialogTitleCommon";
import {FormButton} from "components/FormComponents/Button/FormButton";
import Select, {type SelectChangeEvent} from "@mui/material/Select";
import {formTextFieldStyle} from "../../FormComponents/TextField/formTextFieldStyle";

import {getServerMessages, initialMessage, initialMessages, isValidObject, loggedMessage, messages, type message} from "commonUtils/utils";
import {
  Alerts,
  AxiosErrorCode,
  CPSMessages,
  HttpStatusCode,
  PageNames,
  CPSNumbers,
  CPSMessageCodes,
} from "commonUtils/enumerations";
import {
  HttpException,
  ServiceAuthenticationException,
} from "services/exceptionHandlerService";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import Radio from "@mui/material/Radio";
import OTPComp from "components/OTP/OTPComp";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import {FormTextField} from "components/FormComponents/TextField/FormTextField";

interface MFAPreferred {
  mfaCountryCode: number;
  mfaPreference: number;
  mfaMobile: string;
  mfaEmail: string;
  oTP: string;
}
interface OTPDetail {
  resendCountDown: number;
  otpSentTo: string;
}
interface CountryList {
  id: number;
  countryCode: string;
}
const MFAPreferedDialog = ({
  openDialog,
  handleCloseMFA,
  userId,
}: {
  openDialog: boolean;
  handleCloseMFA: any;
  userId?: number;
}) => {
  const [serverValidation, setServerValidation] =
    React.useState<message>(initialMessage);
    const [serverMessagesList, setServerMessagesList] =
    React.useState<messages[]>(initialMessages);
  const [mfaPreferred, setMFAPreferred] = useState<MFAPreferred>();
  const [oTPDetail, setOTPDetail] = useState<OTPDetail>();
  const [loader, setLoader] = useState<boolean>(false);
  const [showPreferred, setShowPreferred] = useState<boolean>(true);
  const [showOTP, setShowOTP] = useState<boolean>(false);
  const [showOk, setShowOK] = useState<boolean>(false);
  const otpCompRef = useRef(null);
  const countryCodeRef = useRef(null);  
  const btnVerifyRef = useRef(null);
  const [errorMsg, setErrorMsg] = React.useState<string>("");
  const [verifySuccessMessage, setVerifySuccessMessage] =
    React.useState<string>("");
  const [preferredMethod, setPreferredMethod] = useState<number>(1);
  const [countryCodeId, setCountryCodeId] = useState<number>(1);
  const [mobileNumber, setMobileNumber] = useState<string>("");
  const [countryList, setCountryList] = React.useState<CountryList[]>([]);

  const getMessage = (messageCode: number) => {
    const findVal = serverMessagesList?.find(
      (v: any) => v.messageCode === messageCode,
    );
    return findVal ?? initialMessage;
  };

  const getMFAPreferred = async () => {
    try {
      const authState = store.getState();
      setLoader(true);
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };

      const responseData = await postRequest({
        url: COMMON_MFA_PREFERRED,
        headers: setHeader(APIHeaders),
        params: {
          mFAUserId: userId,
        },
        data: {},
      });
      if (isValidObject(responseData)) {
        setMFAPreferred(responseData);
        setPreferredMethod(responseData?.mfaPreference);
        setCountryCodeId(responseData?.mfaCountryCode || 1);
        setMobileNumber(responseData?.mfaMobile);
        setCountryList(responseData?.countryList);
      }
      setLoader(false);
    } catch (error: any) {
      handleError(error, false);
    }
  };
  React.useEffect(() => {
    try {
      LoadMessages();
      getMFAPreferred();
    } catch (error: any) {
      setLoader(false);
      handleAxiosErrors(error);
    }
  }, []);

  const LoadMessages = async () => {
    setServerMessagesList(await getServerMessages(
      CPSMessageCodes.MFA_PREFERRED_MESSAGES,
    ));
  };
  const handleClose = () => {
    handleCloseMFA(false);
  };

  const handleAxiosErrors = (error: any) => {
    const data = {pagename: PageNames.MFAPREFERRED};
    if (error?.code === AxiosErrorCode.ERR_BAD_REQUEST) {
      if (
        error?.response?.status === HttpStatusCode.UNAUTHORIZED ||
        error?.response?.status === HttpStatusCode.FORBIDDEN
      ) {
        ServiceAuthenticationException(error, data, error?.response?.status);
      } else if (error?.response?.status === HttpStatusCode.BAD_REQUEST) {
        setErrorMsg(error.response.data.message);
      } else if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
        setErrorMsg(CPSMessages.SERVICE_NOT_FOUND);
      } else if (
        error?.response?.status === HttpStatusCode.INTERNAL_SERVER_ERROR
      ) {
        HttpException(error, data);
        setErrorMsg(CPSMessages.INTERNAL_SERVER_ERROR);
      } else {
        setErrorMsg(error.message);
      }
    } else if (error?.code === AxiosErrorCode.ECONNABORTED) {
      setErrorMsg(CPSMessages.TIMEOUT_ERROR);
    } else if (error?.code === AxiosErrorCode.ERR_NETWORK) {
      setErrorMsg(CPSMessages.NETWORK_ERROR);
    } else {
      setErrorMsg(error.message);
    }
  };
  const formatInput = (value: string) => {
    value = value.replace(/\D/g, "");
    if(countryCodeId === CPSNumbers.ONE)
    {
      // Format the input value as 123-456-7890
      value = value.replace(/(\d{3})(\d{1,3})?(\d{1,4})?/, "$1-$2-$3").replace(/\D+$/g,"");
      return value.substring(0,12);
    } else {
      return value.substring(0,15);
    }
  };

  const getNewPhone = () => {
    const phoneNumber = mobileNumber?.replace(/\D/g, "");
    return phoneNumber;
  };

  const validateSubmit = (): boolean => {
    setServerValidation(initialMessage);
    let isValid = true;
    const phoneNumber = getNewPhone();
    if (preferredMethod === 2 && !isValidObject(phoneNumber))
    {
      isValid = false;
    } else if (isValidObject(phoneNumber)) {
      if(countryCodeId === 1 && phoneNumber.length < 10) {
        isValid = false;
      } else if (countryCodeId !== 1 && phoneNumber.length < 4) {
        isValid = false;
      }
    }
    if(!isValid){
      setServerValidation(getMessage(7088016));
    }    
    return isValid;
  };
  const sendMFAPhoneOTP = async () => {
    setServerValidation(initialMessage);
    setErrorMsg("");
    const phoneNumber = getNewPhone();
    try {
      const authState = store.getState();
      setLoader(true);
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };

      const responseData = await postRequest({
        url: COMMON_SEND_MFA_PHONE_OTP,
        headers: setHeader(APIHeaders),
        params: {
          userId,
          otpSentTo: `+${countryCodeId} ${phoneNumber}`,
        },
        timeout: 60000,
        data: {},
      });
      if (isValidObject(responseData)) {
        setShowOTP(true);
        setShowPreferred(false);
        setOTPDetail(responseData);
      }
      setLoader(false);
    } catch (error: any) {
      handleError(error, true);
    }
  };
  const handleSubmitClick = () => {
    setServerValidation(initialMessage);
    setErrorMsg("");
    if (!validateSubmit()) {
      return;
    }
    if (isValidObject(mobileNumber) &&
      (countryCodeId !== mfaPreferred?.mfaCountryCode ||
        mobileNumber !== mfaPreferred?.mfaMobile)
    ) {
      sendMFAPhoneOTP();
    } else {
      const authState = store.getState();
      const profileId = authState.auth?.userDetails?.profileId;
      const paramsData = {
        mfaPreference: preferredMethod,
        mfaMobile: mobileNumber,
        mfaCountryCode: countryCodeId,
        mfaEmail: mfaPreferred?.mfaEmail,
        mfaUserId: userId,
        profileId,
      };

      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      setLoader(true);
      submitMFA(paramsData, APIHeaders);
    }
  };
  const handleClickVerify = async () => {
    setServerValidation(initialMessage);
    setErrorMsg("");
    let otpData: string = "";
    if (otpCompRef?.current) {
      const current: any = otpCompRef?.current;
      otpData = current?.verify();
    }
    if (otpData && otpData.length === 6) {
      try{
        const authState = store.getState();
        const profileId = authState.auth?.userDetails?.profileId;
        const paramsData = {
          mfaPreference: preferredMethod,
          mfaMobile: mobileNumber,
          mfaCountryCode: countryCodeId,
          mfaEmail: mfaPreferred?.mfaEmail,
          mfaUserId: userId,
          profileId,
        };
        const APIHeaders = {
          Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
        };
        setLoader(true);
        const responseData = await postRequest({
          url: COMMON_VERIFY_MFA_PHONE_OTP,
          headers: setHeader(APIHeaders),
          params: {
            userId,
            otp: otpData,
          },
          timeout: 60000,
          data: {},
        });

        if (isValidObject(responseData)) {
          if (responseData.status) {
          await submitMFA(paramsData, APIHeaders);
          } else {
          responseData.message && setServerValidation(responseData);
          }
        }
        setLoader(false);
      } catch (error: any) {
        handleError(error, false);
      }
    } else {
      setServerValidation(getMessage(7088017));
    }
  };

  const handleError = (error: any, showCustomMessage: boolean = false) => {
    setLoader(false);
    handleAxiosErrors(error);
    loggedMessage(error, errorMsg, PageNames.MFAPREFERRED);
    if(showCustomMessage)
    {
      setErrorMsg("");
      let message = getMessage(7088011);
      message.isErrorMessage = true;
      message.isValidationMessage =false;
      setServerValidation(message);
    }
  }

  const handleResend = () => {
    setShowOTP(false);
    sendMFAPhoneOTP();
    setShowPreferred(false);
  };
  const submitMFA = async (paramsData: any, APIHeaders: any): Promise<void> => {
    try {
      setLoader(true);
      const responseData = await postRequest({
        url: COMMON_MFA_PREFERREDS,
        headers: setHeader(APIHeaders),
        params: paramsData,
        timeout: 60000,
        data: {},
      });

      if (isValidObject(responseData)) {
        if (responseData.isSuccessMessage && responseData.message) {
          if (showPreferred) {
            handleClose();
          } else {
            setVerifySuccessMessage(responseData.message);
            setShowOK(true);
            setShowOTP(false);
          }
        } else {
          responseData.message && setServerValidation(responseData);
        }
      }
      setLoader(false);
    } catch (error: any) {
      handleError(error, true);
    }
  };

  const handlePhoneChange = (event: SelectChangeEvent<any>) => {
    setCountryCodeId(event.target.value);
  };

  const setFocusToVerify= () => {
    if(btnVerifyRef?.current) {
      const btnVerify: any = btnVerifyRef?.current;
      btnVerify?.focusButton();
    }
  };

  return (
    <>
      <Dialog open={openDialog} fullWidth maxWidth="xs">
        <DialogTitleCommon title="Multi-Factor Authentication Details" />
        <DialogCloseCommon onClick={handleClose} />
        <DialogContent sx={mFADialogStyle.DialogPadding}>
          <Loader open={loader} />
          <Grid>
            {serverValidation?.message && (
              <FormValidationAlert
                severity={
                  serverValidation?.isSuccessMessage ?? false
                    ? Alerts.SUCCESS
                    : serverValidation?.isValidationMessage ?? false
                      ? Alerts.INFO
                      : Alerts.ERROR
                }
                message={serverValidation?.message ?? ""}
              />
            )}
            {errorMsg && (
              <FormValidationAlert severity={Alerts.ERROR} message={errorMsg} />
            )}
            {showPreferred && (
              <Grid>
                <Grid container>
                  <Grid item xs={12} sx={mFADialogStyle.marginBottom}>One preferred authentication method must be entered</Grid>
                  <Grid item xs={2}>
                    <Typography
                      component="p"
                      sx={mFADialogStyle.TypoGraphyTitle}>
                      Preferred
                    </Typography>
                  </Grid>
                  <Grid item xs={2.5}>
                    <Typography
                      component="p"
                      sx={mFADialogStyle.TypoGraphyTitle}>
                      Country
                    </Typography>
                  </Grid>
                  <Grid item xs={7.5}>
                    <Typography
                      component="p"
                      sx={mFADialogStyle.TypoGraphyTitle}>
                      Mobile Number
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Radio
                      checked={preferredMethod === 2}
                      onChange={() => {
                        setPreferredMethod(2);
                      }}
                      value={2}
                      name="radio-buttons"
                      inputProps={{"aria-label": "2"}}
                    />
                  </Grid>
                  <Grid item xs={10} sx={mFADialogStyle.GridItem}>
                    <FormControl
                      sx={mFADialogStyle.selectControl}
                      size="medium">
                      <Select
                        ref={countryCodeRef}
                        sx={mFADialogStyle.selectHeightRadius}
                        value={countryCodeId}
                        onChange={event => {
                          handlePhoneChange(event);
                        }}>
                        {countryList?.map((country: CountryList) => (
                          <MenuItem key={country.id} value={country.id}>
                            {country.countryCode}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      id="mobileNumber"
                      name="mobileNumber"
                      label=""
                      sx={{...formTextFieldStyle.loginmuiTextFieldStyle, ...mFADialogStyle.inputRadius}}
                      value={formatInput(mobileNumber)}
                      onChange={event => {
                        setMobileNumber(formatInput(event.target.value));
                      }}
                    />
                  </Grid>
                  <Grid item xs={2} />
                  <Grid item xs={10}>
                    <Typography
                      component="p"
                      sx={mFADialogStyle.TypoGraphyTitle}>
                      Email
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Radio
                      checked={preferredMethod === 1}
                      onChange={() => {
                        setPreferredMethod(1);
                      }}
                      value={1}
                      name="radio-buttons"
                      inputProps={{"aria-label": "1"}}
                    />
                  </Grid>
                  <Grid item xs={10}>
                    <FormTextField
                      disabled={true}
                      id="email"
                      name="email"
                      label=""
                      value={mfaPreferred?.mfaEmail}
                    />
                  </Grid>
                </Grid>
                <Grid>
                  <Grid item sx={mFADialogStyle.GridItemCenter}>
                    <Box>
                      <FormButton
                        outlet={"submit"}
                        variant="contained"
                        onClick={handleSubmitClick}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {showOTP && (
              <>
                <Grid item>
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: oTPDetail?.otpSentTo ?? "",
                    }}></Box>
                </Grid>
                <Grid item>
                  <Box sx={mFADialogStyle.otpContainer}>
                    <OTPComp
                      ref={otpCompRef}
                      length={6}
                      handleOtpSent={(e: any, preferredMethod: number) => {
                        handleResend();
                      }}
                      resendCountDown={oTPDetail?.resendCountDown ?? 0}
                      lbltext="Did not receive code?"
                      otpSentMethod={2}
                      handleFocusNext={() => {setFocusToVerify()}}
                    />
                  </Box>
                </Grid>
                <Grid>
                  <Grid item sx={mFADialogStyle.GridItemCenter}>
                    <Box>
                      <FormButton
                        ref={btnVerifyRef}
                        outlet={"Verify"}
                        variant="contained"
                        onClick={handleClickVerify}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
            {showOk && (
              <Grid>
                <Grid item sx={mFADialogStyle.GridItemCenter}>
                <Typography
                      component="p"
                      sx={mFADialogStyle.TypoGraphyMsgCenter}
                      dangerouslySetInnerHTML={{
                        __html: verifySuccessMessage ?? "",
                      }}/>
                </Grid>
                <Grid item sx={mFADialogStyle.GridItemCenter}>
                  <Box>
                    <FormButton
                      outlet={"Ok"}
                      variant="contained"
                      onClick={handleClose}
                    />
                  </Box>
                </Grid>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MFAPreferedDialog;
